import React, { useState } from 'react';
import styled from 'styled-components';

const StarRatingContainer = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 2px;
`;

const Star = styled.span`
    color: ${props => props.filled ? '#ffa500' : '#e0e0e0'};
    font-size: ${props => props.size === 'small' ? '18px' : '24px'};
    cursor: pointer;
    transition: color 0.15s ease, transform 0.15s ease;
    user-select: none;

    &:hover {
        transform: scale(1.1);
    }
`;

const ClearButton = styled.span`
    color: #999;
    font-size: ${props => props.size === 'small' ? '14px' : '18px'};
    cursor: pointer;
    margin-right: 6px;
    transition: color 0.15s ease, transform 0.15s ease;
    user-select: none;
    opacity: 0.6;

    &:hover {
        color: #e74c3c;
        transform: scale(1.1);
        opacity: 1;
    }
`;

const RatingLabel = styled.span`
    margin-left: 8px;
    font-size: 14px;
    color: #666;
`;

/**
 * Component hiển thị rating với sao có thể click để chọn
 * @param {number} value - Giá trị rating hiện tại (0-5, 0 = chưa đánh giá)
 * @param {Function} onChange - Callback khi rating thay đổi
 * @param {number} maxStars - Tổng số sao tối đa (default: 5)
 * @param {string} size - Kích thước: 'small' hoặc 'normal' (default: 'normal')
 * @param {boolean} showLabel - Hiển thị label số sao (default: true)
 * @param {boolean} disabled - Vô hiệu hóa tương tác (default: false)
 * @param {boolean} allowClear - Cho phép xóa rating về 0 (default: false)
 */
const InteractiveStarRating = ({
    value = 0,
    onChange,
    maxStars = 5,
    size = 'normal',
    showLabel = true,
    disabled = false,
    allowClear = false
}) => {
    const [hoverValue, setHoverValue] = useState(0);

    const handleClick = (rating) => {
        if (!disabled && onChange) {
            // Nếu click vào sao đã chọn và allowClear = true, clear về 0
            if (allowClear && rating === value) {
                onChange(0);
            } else {
                onChange(rating);
            }
        }
    };

    const handleClear = () => {
        if (!disabled && onChange) {
            onChange(0);
        }
    };

    const handleMouseEnter = (rating) => {
        if (!disabled) {
            setHoverValue(rating);
        }
    };

    const handleMouseLeave = () => {
        setHoverValue(0);
    };

    const displayValue = hoverValue || value;

    const stars = [];
    for (let i = 1; i <= maxStars; i++) {
        stars.push(
            <Star
                key={i}
                filled={i <= displayValue}
                size={size}
                onClick={() => handleClick(i)}
                onMouseEnter={() => handleMouseEnter(i)}
                onMouseLeave={handleMouseLeave}
                style={{ cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.6 : 1 }}
                title={allowClear && i === value ? 'Click to clear' : `${i} star${i > 1 ? 's' : ''}`}
            >
                ★
            </Star>
        );
    }

    const getLabelText = (rating) => {
        if (rating === 1) return '1 star';
        return `${rating} stars`;
    };

    return (
        <StarRatingContainer>
            {allowClear && value > 0 && !disabled && (
                <ClearButton
                    size={size}
                    onClick={handleClear}
                    title="Clear rating"
                >
                    ✕
                </ClearButton>
            )}
            {stars}
            {showLabel && displayValue > 0 && (
                <RatingLabel>{getLabelText(displayValue)}</RatingLabel>
            )}
        </StarRatingContainer>
    );
};

export default InteractiveStarRating;
